import { useState, useMemo, useEffect, useCallback } from 'react';
import styles from './CompletedLessonsModal.module.scss';
import { Lesson, ExtendedHeaUnit } from '@types';
import PendingModal from './modal/PendingModal';
import {
  ContentType,
  StudyPlanContentFragment,
  useHeaConfirmLessonMutation,
  useHeaLessonsToConfirmLazyQuery,
  useHeaUnlockAccountMutation
} from '../../api/graphql-codegen';
import Spinner from '../spinner/Spinner';
import LessonCompleteForm from './lesson-form/LessonCompleteForm';
import { CourseContentProvider, useCourseContent } from '@hooks/useCourseContent';
import SelectedContent from '../program-details/selected-content/SelectedContent';
import LessonPreView from './lesson-form/detail/LessonPreView';
import { isHomeEDApp, parentPlanName } from '@constants';
import CloseIcon from '../../assets/icons/close.svg?react';
import ReviewModal from './review-modal/ReviewModal';
import LessonRunModal from '../program-details/lesson-run-modal/LessonRunModal';
import UnitProgressModal from './UnitProgressModal';
import LockModal from '../program-details/lock-modal/LockModal';
import SuccessModal from '../program-details/success-modal/SuccessModal';
import { useAuthToken } from '@src/hooks/useAuthToken';
import ConfirmPasswordModal from './confirm-modal/ConfirmPasswordModal';

type Props = {
  open: boolean;
  onClose: () => void;
  heaUnitData?: ExtendedHeaUnit;
  isDashboard?: boolean;
  isParentApp?: boolean;
  parentSkill?: string;
  isEditMode?: boolean;
  studyPlansForSelectedUnit?: StudyPlanContentFragment[];
  addUnitToStudyPlan?: () => void;
  gotoStudyMode?: (studyPlan: StudyPlanContentFragment) => void;
  studyPlans?: StudyPlanContentFragment[];
  heaUnits?: ExtendedHeaUnit[];
  reviewLessonId?: string;
  studyPlanId?: string;
};

function selectFirstContentExceptSurvey(modules: any[]) {
  if (modules.length > 0) {
    if (modules.at(0).type === ContentType.FORM) {
      modules.shift()
      return selectFirstContentExceptSurvey(modules)
    } else {
      return modules.at(0);
    }
  } else {
    return undefined
  }
}

function CompletedLessonsComponent(props: Props) {
  const { open, onClose, heaUnitData, isParentApp, studyPlansForSelectedUnit, addUnitToStudyPlan, studyPlans, heaUnits, reviewLessonId, studyPlanId } = props;

  const [showLessonView, setShowLessonView] = useState(false)
  const [showLessonPreView, setShowLessonPreView] = useState(false)
  const [resumeModal, setResumeModal] = useState(false)
  const [lastPageNum, setLastPageNum] = useState(1)

  const [movedScroll, setMovedScroll] = useState(false)
  const [lockModal, setLockModal] = useState(false)
  const [activated, setActivated] = useState(false)
  const [lessonRunModal, setLessonRunModal] = useState(false)
  const [showConfirmCompleteModal, setShowConfirmCompleteModal] = useState(false)
  const [completeNotify, setCompleteNotify] = useState(false)
  const [getBack, setGetBack] = useState(false)
  const [fadeType, setFadeType] = useState("in")
  const { setLockAccount, password } = useAuthToken()
  const {
    content: selectedContent,
    lesson,
    setContent,
    setLesson,
    setIsParent,
    setIsPreview,
    setIsViewExitTicket,
    currentStudyplan,
    setCurrentStudyplan,
    showReview,
    setShowReview,
    showCompleteUnit,
    setShowCompleteUnit,
    accountLocked,
    setAccountLocked,
    setIsConfirmView,
    currentHeaUnit,
    setCurrentHeaUnit,
  } = useCourseContent();

  const [heaLessonsToConfirm, {loading, data, refetch}] = useHeaLessonsToConfirmLazyQuery({ fetchPolicy: 'network-only',
    onCompleted(res) {
      setCompleteNotify(false)
      if (accountLocked) {
        setShowConfirmCompleteModal(true);
      }
      if (getBack && res.heaLessonsToConfirm?.length === 0) {
        setGetBack(false)
        if (!accountLocked) {
          setFadeType("out")
        }
        // onClose()
      }
    }
  });
  const [heaUnlockAccount, {loading: unlocking}] = useHeaUnlockAccountMutation({
    onCompleted: (res) => {
      if (res.heaUnlockAccount) {
        setAccountLocked(false);
        setShowConfirmCompleteModal(false);
        if (confirmList.length === 0) {
          setFadeType("out");
        }
      }
    }
  })

  const unlockAccount = useCallback(() => {
    heaUnlockAccount({
      variables: { input: { passcode: password ?? "" } },
    });
  }, [heaUnlockAccount, password])

  const [heaConfirmLesson] = useHeaConfirmLessonMutation();
  const markLessonAsInComplete = useCallback(async() => {
    if (!currentStudyplan?.id || !lesson) {
      return;
    }
    const res = await heaConfirmLesson({
      variables: {
        input: {
          studyplanId: currentStudyplan.id,
          lessonId: lesson.id,
          completed: false
        }
      }
    });
    if (res?.data?.heaConfirmLesson) {
      refetch();
    }
  }, [lesson, currentStudyplan?.id, heaConfirmLesson, refetch]);

  const confirmList = useMemo(() => [...data?.heaLessonsToConfirm || []], [data?.heaLessonsToConfirm])
  useEffect(() => {
    setIsConfirmView(true);
    heaLessonsToConfirm();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) {
      setFadeType("out")
    }
  }, [open])

  const transitionEnd = (e: any) => {
    if (e.propertyName !== "opacity" || fadeType === "in") return;
    if (fadeType === "out") {
      onClose();
    }
  }

  useEffect(() => {
    setIsParent(!!isParentApp);
  }, [isParentApp, setIsParent]);

  const handleRefetch = useCallback((complete: boolean) => {
    if (complete) {
      setCompleteNotify(false);
      setGetBack(true);
    }
    refetch();
  }, [refetch, setGetBack]);

  const handleScroll = useCallback(() => {

    setMovedScroll(current => !current)
  }, [])

  const handleClose = useCallback(() => {
    if (showLessonView || resumeModal || showLessonPreView) {
      setResumeModal(false);
      setShowLessonView(false);
      setShowLessonPreView(false);
      document.getElementById("modal-body")!.style.overflow = "auto"
    } else {
      setGetBack(false);
      setFadeType("out")
      // onClose();
    }
  }, [resumeModal, showLessonPreView, showLessonView]);

  const handleOpenLessonWithChilds = useCallback((lesson: Lesson) => {
    setLesson(lesson);

  }, [setLesson]);

  const handleOpenLesson = useCallback((lesson: Lesson) => {
    setLesson(lesson)
    if (lesson.modules) {
      if (isHomeEDApp) {
        setContent(selectFirstContentExceptSurvey(lesson.modules))
      } else {
        setContent(lesson.modules.at(0) as any)
      }
      setLastPageNum(1)
    }
    setIsViewExitTicket(false);
    setShowLessonView(true);
  }, [setLesson, setShowLessonView, setContent, setIsViewExitTicket]);

  const handleOpenExitTicket = useCallback((lesson: Lesson, studyPlan: StudyPlanContentFragment) => {
    setLesson(lesson);
    setCurrentStudyplan(studyPlan);
    const unitId = confirmList.find(item => item.lesson?.id === lesson.id)?.unitId;
    if (unitId) {
      const unitData = heaUnits?.find(unit => unit.id === unitId);
      if (unitData) {
        setCurrentHeaUnit(unitData);
      }
    }
    if (lesson.modules) {
      const exitTicketForm = lesson.modules.find(c => c.type === ContentType.FORM);
      if (exitTicketForm) {
        setContent(exitTicketForm)
      } else {
        const exitTicketSlide = lesson.modules.find(c => c.name.toLowerCase().includes('exit ticket'));
        if (exitTicketSlide) {
          setContent(exitTicketSlide)
        }
      }
      setLastPageNum(1)
    }
    setIsViewExitTicket(true);
    setShowLessonView(true);
  }, [confirmList, heaUnits, setCurrentHeaUnit, setLesson, setShowLessonView, setContent, setIsViewExitTicket, setCurrentStudyplan]);

  const handleCloseLesson = useCallback(() => {
    setGetBack(true)
    document.getElementById("modal-body")!.style.overflow = "auto"
    setShowLessonView(false);
    heaLessonsToConfirm()
  }, [heaLessonsToConfirm])

  const handleStartLessons = useCallback((lesson: Lesson) => {
    setIsPreview(false);
    setLesson(lesson)
    if (lesson.modules) {
      if (isHomeEDApp) {
        setContent(selectFirstContentExceptSurvey(lesson.modules))
      } else {
        setContent(lesson.modules.at(0) as any)
      }
      setLastPageNum(1)
    }
    setShowLessonPreView(false);
    setIsViewExitTicket(false);
    setShowLessonView(true);

  }, [setContent, setLesson, setIsPreview, setIsViewExitTicket]);

  const handleLessonPreview = useCallback((lesson: Lesson, studyPlan: StudyPlanContentFragment) => {
    setTimeout(()=>{
      setIsPreview(true);
      setCurrentStudyplan(studyPlan);
      setIsViewExitTicket(false);
      setLesson(lesson)
      const unitId = confirmList.find(item => item.lesson?.id === lesson.id)?.unitId;
      if (unitId) {
        const unitData = heaUnits?.find(unit => unit.id === unitId);
        if (unitData) {
          setCurrentHeaUnit(unitData);
        }
      }
      if (lesson.modules) {
        if (isHomeEDApp) {
          setContent(selectFirstContentExceptSurvey(lesson.modules))
        } else {
          setContent(lesson.modules.at(0) as any)
        }
        setLastPageNum(1)
      }
      setShowLessonPreView(true);
    }, 200)
  }, [confirmList, heaUnits, setCurrentHeaUnit, setContent, setLesson, setIsPreview, setIsViewExitTicket, setCurrentStudyplan]);

  const handleCloseLessonPreview = useCallback(() => {
    setIsPreview(false);
    setShowLessonPreView(false);
    document.getElementById("modal-body")!.style.overflow = "auto"
  }, [setIsPreview]);

  const handleCloseLessonModal = useCallback(() => {
    document.getElementById("modal-body")!.style.overflow = "auto"
    setLessonRunModal(false)
  }, []);

  const handleOpenLessonWithYou = useCallback((studyPlan: StudyPlanContentFragment, lessonData?: Lesson) => {
    const startLesson = lessonData || lesson;
    if (!lesson && startLesson) {
      setLesson(startLesson as Lesson)
    }
    setTimeout(() => {
      if (startLesson?.modules) {
        // setContent(lesson.modules.at(0) as any)
        setContent(selectFirstContentExceptSurvey(startLesson.modules))
        setLastPageNum(1)
      }
      setCurrentStudyplan(studyPlan);
      setShowLessonView(true);
      setLessonRunModal(false);
    }, 200)
  }, [lesson, setLesson, setCurrentStudyplan, setContent]);

  const handleLockWithStudyPlan = useCallback((studyPlan: StudyPlanContentFragment) => {
    if (lesson) {
      setLesson(lesson as Lesson)
    }
    setCurrentStudyplan(studyPlan);
    setLessonRunModal(false);
    setLockModal(true);
  }, [lesson, setLesson, setCurrentStudyplan]);

  const handleCloseLockModal = useCallback(() => {
    document.getElementById("modal-body")!.style.overflow = "auto"
    setLockModal(false)
  }, []);

  const handleActivated = useCallback(() => {
    document.getElementById("modal-body")!.style.overflow = "auto"
    setLockModal(false);
    setActivated(true);
    setAccountLocked(true);
    setLockAccount('lock');
  }, [setLockAccount, setAccountLocked]);

  const handleActiveSuccess = useCallback(() => {
    document.getElementById("modal-body")!.style.overflow = "auto"
    setLessonRunModal(false)
    setActivated(false);
    if (lesson) {
      setTimeout(() => {
        if (lesson.modules) {
          setContent(selectFirstContentExceptSurvey(lesson.modules))
          setLastPageNum(1)
        }
        setShowLessonView(true);
      }, 200)
    }
  }, [lesson, setContent])

  const handleReviewLesson = (lesson: Lesson, studyPlan: StudyPlanContentFragment) => {
    setLesson(lesson);
    setCurrentStudyplan(studyPlan)
    setIsViewExitTicket(false);
    setShowReview(true);
  }

  const handleReviewNow = useCallback((lesson: Lesson) => {
    setLesson(lesson);
    setIsViewExitTicket(false);
    setShowReview(false);
    setLessonRunModal(true);

    markLessonAsInComplete();
  }, [markLessonAsInComplete, setIsViewExitTicket, setLesson, setShowReview]);

  useEffect(() => {
    if (reviewLessonId && studyPlanId && confirmList?.length && studyPlans?.length) {
      const lessonData = confirmList.find(item => item.lesson?.id === reviewLessonId)?.lesson as Lesson;
      const studyPlan = studyPlans?.find(plan => plan.id === studyPlanId);
      if (lessonData && studyPlan) {
        setLesson(lessonData);
        setCurrentStudyplan(studyPlan)
        // setIsViewExitTicket(false);
        // handleOpenLessonWithYou(studyPlan, lessonData);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewLessonId, studyPlanId, confirmList?.length, studyPlans?.length, setLesson, setShowReview]);

  const handleReviewLater = useCallback((lesson: Lesson) => {
    setLesson(lesson);
    setIsViewExitTicket(false);
    setShowReview(false);

    markLessonAsInComplete();
  }, [markLessonAsInComplete, setIsViewExitTicket, setLesson, setShowReview])

  const handleCloseReviewModal = () => {
    setIsViewExitTicket(false);
    setShowReview(false);
  }

  const handleConfirmPassword = () => {
    unlockAccount()
  }

  const handleClosePasswordModal = () => {
    setShowConfirmCompleteModal(false);
  }

  const filteredStudyPlans = useMemo(() => {
    var studyplans = [...studyPlans || []].filter(plan => !plan.name.includes(parentPlanName))
    studyplans = studyplans.filter(plan => plan.name.includes(currentStudyplan?.name || ""))
    return studyplans
  }, [studyPlans, currentStudyplan])

  return (
    <PendingModal open={open} onClose={handleClose} onScroll={handleScroll} closeButton={false} isLoading={loading} fade={fadeType} fadeAction={transitionEnd}>
      {completeNotify && <div className={styles.notifyView}>
        <div className={styles.notifyTitle}>
          <span><strong>Lesson Status</strong> update to <strong>Completed!</strong></span>
        </div>
      </div>}
      {loading ? <div className={styles.loading}><Spinner /></div> :
      <div className={styles.wrapper} style={{position: (showLessonView || showLessonPreView) ? 'unset' : 'relative'}}>
        <div className={styles.backdrop} onClick={handleClose}/>
        <div id="lessonform" className={styles.lessonView}>
          <div className={styles.lessonSubView}>
            <div className={styles.lessonTitle}>Please <strong>Approve Pending</strong> Lessons</div>
            <button onClick={handleClose} className={styles.closeWrapper}>
              <CloseIcon className={styles.closeIcon}/>
            </button>
            <div className={styles.lessonListView}>
            {confirmList.length === 0 && <div className={styles.noLessonTitle}>
              <span>{"There are no Lessons to review."}</span>
            </div>}
            {confirmList.map((child, index) => {
              const studyPlan = studyPlans?.find(plan => plan.id === child.studyplanId)
              return (
                <div key={index} className={styles.lessonMargin}>
                  <LessonCompleteForm
                    index={index} lesson={child.lesson as Lesson} openDetails={handleOpenLesson} openLessonWithChilds={handleOpenLessonWithChilds}
                    openPreview={handleLessonPreview} unitId={child.unitId} isParentApp={isParentApp} studyPlansForSelectedUnit={studyPlansForSelectedUnit}
                    studyPlan={studyPlan} isParentUnit={heaUnitData?.ageGroup?.includes('Parent & Caregiver')} movedScroll={movedScroll} child={child}
                    openExitTicket={handleOpenExitTicket} refetch={handleRefetch} incompleted={child.incomplete ?? false} reviewNow={handleReviewNow} reviewLesson={handleReviewLesson}
                  />
                </div>
              )}
            )}
            </div>
          </div>
        </div>
        <div className={styles.empty}>&nbsp;</div>
        {(showLessonView && selectedContent) ?
        <div className={styles.teacherFSView}>
          <div className={styles.fsBackdrop} onClick={handleClose}/>
          <div className={styles.lessonFSSubView}>
            <SelectedContent lesson={lesson} content={selectedContent} unit={currentHeaUnit} goBack={handleCloseLesson} lastPageNum={lastPageNum} isParentApp={isParentApp} studyPlanId={currentStudyplan?.id} />
          </div>
        </div> : null}
        {(showLessonPreView && selectedContent && currentHeaUnit && lesson) ?
          <div className={styles.teacherFSView}>
            <div className={styles.fsBackdrop} onClick={handleCloseLessonPreview}/>
            <div className={styles.teacherFSSubView}>
              <LessonPreView
                unit={currentHeaUnit} lesson={lesson} onClose={handleCloseLessonPreview}
                onExit={onClose} startLesson={handleStartLessons} isParentApp={isParentApp}
                studyPlansForSelectedUnit={filteredStudyPlans} isConfirmCompletion={true}
                addUnitToStudyPlan={addUnitToStudyPlan} refetch={handleRefetch}
              />
            </div>
        </div> : null}
      </div>}
      {activated && <SuccessModal open={activated} studyPlan={currentStudyplan?.name} onClose={() => setActivated(false)} onSuccess={handleActiveSuccess}/>}
      {lockModal && <LockModal studyPlan={currentStudyplan} open={lockModal} onClose={handleCloseLockModal} onContinue={handleActivated} />}
      {lessonRunModal && <LessonRunModal open={lessonRunModal} studyPlansForSelectLesson={filteredStudyPlans} state={2} onClose={handleCloseLessonModal} onContinueWithYou={handleOpenLessonWithYou} onContinueWithChild={handleLockWithStudyPlan} />}
      {showConfirmCompleteModal && <ConfirmPasswordModal open={showConfirmCompleteModal} lesson={lesson} onConfirmPassword={handleConfirmPassword} onClose={handleClosePasswordModal} message='Please enter your Password to Unlock the Account' closeButton={false} loading={unlocking} />}
      {showReview && <ReviewModal open={showReview} onClose={handleCloseReviewModal} onReviewLater={handleReviewLater} onReviewNow={handleReviewNow}/>}
      {(showCompleteUnit && currentHeaUnit) && <UnitProgressModal open={showCompleteUnit} onClose={() => setShowCompleteUnit(false)} unitId={currentHeaUnit?.id ?? ""} program={undefined} heaUnitData={currentHeaUnit} isDashboard={false} />}
    </PendingModal>
  );
}

const CompletedLessonsModal = (props: Props) => {
  const { open, onClose, heaUnitData, isDashboard, isParentApp, isEditMode, parentSkill, studyPlansForSelectedUnit, studyPlans, heaUnits, gotoStudyMode, reviewLessonId, studyPlanId } = props;

  return (
    <CourseContentProvider>
      <CompletedLessonsComponent
        open={open} onClose={onClose}
        heaUnitData={heaUnitData} studyPlans={studyPlans} heaUnits={heaUnits}
        isDashboard={isDashboard} isParentApp={isParentApp} parentSkill={parentSkill} isEditMode={isEditMode}
        studyPlansForSelectedUnit={studyPlansForSelectedUnit} gotoStudyMode={gotoStudyMode} reviewLessonId={reviewLessonId} studyPlanId={studyPlanId}
      />
    </CourseContentProvider>
  );
}

export default CompletedLessonsModal;
